'use client'

import type { Entry } from '@/document'
import {
  FilmSlateIcon,
  BookOpenIcon,
  MusicNoteIcon,
} from '@phosphor-icons/react'
import { MovieCard } from './movie-card'
import { BookCard } from './book-card'
import { ArtistCard } from './artist-card'
import { LabelCard } from './label-card'

export function MoviesGallery({ movies }: { movies: Entry[] }) {
  if (movies.length === 0) {
    return (
      <EmptyState
        icon={FilmSlateIcon}
        title="No movies in your library"
        description="Capture a movie by typing its name in the main form—try 'Inception' or 'The Matrix'"
      />
    )
  }

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 lg:grid-cols-4">
      {movies.map((movie) => (
        <MovieCard key={movie.id} movie={movie} />
      ))}
    </div>
  )
}

export function BooksGallery({ books }: { books: Entry[] }) {
  if (books.length === 0) {
    return (
      <EmptyState
        icon={BookOpenIcon}
        title="No books in your library"
        description="Capture a book by typing its title—try 'Atomic Habits' or 'Dune'"
      />
    )
  }

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 lg:grid-cols-4">
      {books.map((book) => (
        <BookCard key={book.id} book={book} />
      ))}
    </div>
  )
}

export function MusicGallery({
  artists,
  labels,
}: {
  artists: Entry[]
  labels: Entry[]
}) {
  const isEmpty = artists.length === 0 && labels.length === 0

  if (isEmpty) {
    return (
      <EmptyState
        icon={MusicNoteIcon}
        title="No music in your library"
        description="Capture an artist, album, or label—try 'Bonobo' or 'Blue Note Records'"
      />
    )
  }

  // Combine all music items into a single gallery
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 lg:grid-cols-4">
      {artists.map((artist) => (
        <ArtistCard key={artist.id} artist={artist} />
      ))}
      {labels.map((label) => (
        <LabelCard key={label.id} label={label} />
      ))}
    </div>
  )
}

function EmptyState({
  icon: Icon,
  title,
  description,
}: {
  icon: React.ElementType
  title: string
  description: string
}) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center sm:py-16">
      <Icon className="text-muted-foreground/30 h-10 w-10 sm:h-12 sm:w-12" />
      <h3 className="text-foreground mt-3 text-sm font-medium sm:mt-4">
        {title}
      </h3>
      <p className="text-muted-foreground mt-1 max-w-xs text-xs">
        {description}
      </p>
    </div>
  )
}
